import React, { useMemo, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { Lock, Landmark, Flame } from "lucide-react";
import Reserve from "./Reserve";
import Treasury from "./Treasury";
import Burns from "./Burns";

const TABS = [
  {
    key: "locked",
    label: "Locked Vault",
    icon: Lock,
    tone: "border-sky-500 text-sky-300 bg-sky-500/5",
    hint: "51% of every mint — sharded across 22K humanitarian reserves, no authority path, permanent.",
    Component: Reserve,
  },
  {
    key: "treasury",
    label: "Treasury",
    icon: Landmark,
    tone: "border-amber-500 text-amber-300 bg-amber-500/5",
    hint: "5% of every mint — funds ongoing development. Architect-controlled, on-chain audit trail.",
    Component: Treasury,
  },
  {
    key: "burns",
    label: "Burns",
    icon: Flame,
    tone: "border-rose-500 text-rose-300 bg-rose-500/5",
    hint: "5% of every mint destroyed at the source · plus membership burns. Deflationary.",
    Component: Burns,
  },
];

export default function Reserves() {
  const location = useLocation();
  const navigate = useNavigate();
  const initialTab = useMemo(() => {
    const q = new URLSearchParams(location.search).get("tab");
    return TABS.some((t) => t.key === q) ? q : "locked";
  }, [location.search]);
  const [tab, setTab] = useState(initialTab);
  const active = TABS.find((t) => t.key === tab) || TABS[0];
  const Body = active.Component;

  const switchTab = (key) => {
    setTab(key);
    const q = new URLSearchParams(location.search);
    q.set("tab", key);
    navigate({ search: `?${q.toString()}` }, { replace: true });
  };

  return (
    <div className="min-h-screen bg-[#0a0a0b] text-zinc-100" data-testid="reserves-page">
      <div className="max-w-6xl mx-auto px-4 sm:px-6 pt-6 pb-2">
        <div className="text-[10px] uppercase tracking-[0.35em] text-zinc-500 mb-2">
          System pools
        </div>
        <h1 className="font-heading text-3xl sm:text-4xl tracking-[0.15em] text-zinc-100">
          Reserves
        </h1>
        <p className="text-zinc-400 text-sm mt-2 max-w-2xl leading-relaxed">
          Every verified truth mints AEGIS into three system pools plus the
          submitter&apos;s wallet. Below: the three system pools — locked,
          spendable-by-architect, and destroyed forever — each with their own
          on-chain audit trail.
        </p>

        {/* Tab strip */}
        <div
          role="tablist"
          className="mt-6 flex flex-wrap gap-2"
          data-testid="reserves-tabs"
        >
          {TABS.map((t) => {
            const Icon = t.icon;
            const isActive = t.key === tab;
            return (
              <button
                key={t.key}
                role="tab"
                aria-selected={isActive}
                onClick={() => switchTab(t.key)}
                data-testid={`reserves-tab-${t.key}`}
                className={`inline-flex items-center gap-2 px-4 py-2 border text-[11px] uppercase tracking-[0.25em] transition-colors ${
                  isActive
                    ? t.tone
                    : "border-zinc-800 text-zinc-400 hover:border-zinc-600 hover:text-zinc-200"
                }`}
              >
                <Icon className="h-3.5 w-3.5" />
                {t.label}
              </button>
            );
          })}
        </div>

        {/* Active-tab hint */}
        <div className="mt-3 text-[11px] text-zinc-500 font-mono leading-relaxed max-w-3xl">
          {active.hint}
        </div>
      </div>

      {/* Divider before mounted body */}
      <div className="max-w-6xl mx-auto px-4 sm:px-6">
        <div className="h-px bg-zinc-900 my-2" />
      </div>

      {/* Mount the existing sub-page component with its own layout */}
      <div data-testid={`reserves-body-${active.key}`}>
        <Body />
      </div>
    </div>
  );
}
